/* ============================================================
   LoginRegisterModal.jsx — Login/Register Modal
   Fully working with API doc, inline error/success, loading state
   ============================================================ */
import React, { useState } from "react";
import { createPortal } from "react-dom";
import "./LoginRegisterModal.css";
import { useAuth } from "./AuthContext";
import { sendForgotPasswordOTP, verifyOTP, resetPassword } from "../../services/apiService";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_REGEX = /^[a-zA-Z\s.'-]{3,50}$/;

function isValidPhone(raw) {
  const digits = (raw || "").replace(/\D/g, "");
  const last10 = digits.slice(-10);
  return /^[6-9]\d{9}$/.test(last10);
}

function isValidIdentifier(raw) {
  const val = (raw || "").trim();
  if (!val) return false;
  if (val.includes("@")) return EMAIL_REGEX.test(val);
  return isValidPhone(val);
}

function getPasswordChecks(pwd) {
  return [
    { label: "Min. 8 characters", ok: pwd.length >= 8 },
    { label: "Uppercase letter (A-Z)", ok: /[A-Z]/.test(pwd) },
    { label: "Lowercase letter (a-z)", ok: /[a-z]/.test(pwd) },
    { label: "Number (0-9)", ok: /[0-9]/.test(pwd) },
    { label: "Special character (!@#$…)", ok: /[^A-Za-z0-9]/.test(pwd) },
  ];
}

export default function LoginRegisterModal({ isOpen, onClose }) {
  const { login, register } = useAuth();
  const [activeTab, setActiveTab] = useState("login");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inlineMsg, setInlineMsg] = useState(null); // { type: "success"|"error", text: string }
  const [fieldErrors, setFieldErrors] = useState({});

  // Login fields
  const [identifier, setIdentifier] = useState(""); // email or phone
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);

  React.useEffect(() => {
    let interval;
    if (activeTab === "verify_otp" && otpTimer > 0) {
      interval = setInterval(() => setOtpTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [activeTab, otpTimer]);

  const handleResendOTP = async () => {
    if (otpTimer > 0) return;
    setLoading(true);
    try {
      const res = await sendForgotPasswordOTP(identifier);
      if (res && res.Status) {
        showMsg("success", "OTP Resent Successfully");
        setOtpTimer(120);
      } else {
        showMsg("error", res?.Message || "Failed to resend OTP");
      }
    } catch (err) {
      showMsg("error", "Error resending OTP");
    } finally {
      setLoading(false);
    }
  };

  // Register fields
  const [name, setName] = useState("");

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [isOpen]);

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  if (!isOpen) return null;

  function showMsg(type, text) {
    setInlineMsg({ type, text });
    if (type === "success") {
      setTimeout(() => setInlineMsg(null), 2500);
    }
  }

  function resetRegisterFields() {
    setName("");
    setPhone("");
    setRegPassword("");
    setConfirmPassword("");
    setDob("");
    setGender("");
  }

  function switchTab(tab) {
    setActiveTab(tab);
    setInlineMsg(null);
    setFieldErrors({});
    setPassword("");
    setOtp("");
    setNewPassword("");
    setRegPassword("");
    setConfirmPassword("");
  }

  function validateLogin() {
    const errors = {};
    if (!identifier.trim()) {
      errors.identifier = "Email or mobile number is required.";
    } else if (!isValidIdentifier(identifier)) {
      errors.identifier = "Enter a valid email or 10-digit mobile number.";
    }
    if (!password) {
      errors.password = "Password is required.";
    }
    return errors;
  }

  function validateRegister() {
    const errors = {};
    if (!name.trim()) {
      errors.name = "Full name is required.";
    } else if (!NAME_REGEX.test(name.trim())) {
      errors.name = "Enter a valid name (letters only, min 3 characters).";
    }
    if (!email.trim()) {
      errors.email = "Email address is required.";
    } else if (!EMAIL_REGEX.test(email.trim())) {
      errors.email = "Enter a valid email address.";
    }
    if (!phone.trim()) {
      errors.phone = "Mobile number is required.";
    } else if (!isValidPhone(phone)) {
      errors.phone = "Enter a valid 10-digit mobile number.";
    }
    if (!regPassword) {
      errors.regPassword = "Password is required.";
    } else if (getPasswordChecks(regPassword).some((c) => !c.ok)) {
      errors.regPassword = "Password doesn't meet all the requirements below.";
    }
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password.";
    } else if (confirmPassword !== regPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }
    return errors;
  }

  async function handleLogin(e) {
    e.preventDefault();
    const errors = validateLogin();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      showMsg("error", Object.values(errors)[0]);
      return;
    }
    setLoading(true);
    setInlineMsg(null);
    const result = await login(identifier, password);
    setLoading(false);
    if (result?.success) {
      showMsg("success", "Login successful! Welcome back.");
      setTimeout(() => onClose(), 1000);
    } else {
      // Show the real server message (e.g. "Invalid password") right in
      // the modal, not just the corner toast.
      showMsg(
        "error",
        result?.message || "Invalid email/mobile number or password."
      );
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    const errors = validateRegister();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      showMsg("error", Object.values(errors)[0]);
      return;
    }
    setLoading(true);
    setInlineMsg(null);
    const res = await register(
      name.trim(),
      email.trim(),
      phone.trim(),
      regPassword,
      dob,
      gender
    );
    setLoading(false);
    if (res && res.success) {
      showMsg("success", res.message || "Registration successful! Please login to continue.");
      const registeredEmail = email.trim();
      resetRegisterFields();
      setTimeout(() => {
        switchTab("login");
        setIdentifier(registeredEmail);
      }, 1500);
    } else {
      showMsg("error", res?.message || "Registration failed. Please try again.");
    }
  }

  async function handleSendOTP(e) {
    e.preventDefault();
    if (!identifier.trim() || !EMAIL_REGEX.test(identifier.trim())) {
      setFieldErrors({ identifier: "Enter a valid registered email address." });
      return;
    }
    setLoading(true);
    setInlineMsg(null);
    try {
      const res = await sendForgotPasswordOTP(identifier.trim());
      if (res.Status || res.StatusCode === 200) {
        showMsg("success", res.Message || "OTP Sent Successfully");
        setActiveTab("verify_otp");
      } else {
        showMsg("error", res.Message || "Failed to send OTP");
      }
    } catch (err) {
      showMsg("error", "An error occurred");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOTP(e) {
    e.preventDefault();
    if (!otp.trim() || otp.length < 6) {
      setFieldErrors({ otp: "Enter a valid 6-digit OTP." });
      return;
    }
    setLoading(true);
    setInlineMsg(null);
    try {
      const res = await verifyOTP(identifier.trim(), otp.trim());
      if (res.Status || res.StatusCode === 200) {
        showMsg("success", res.Message || "OTP Verified Successfully");
        setActiveTab("reset_password");
      } else {
        showMsg("error", res.Message || "Invalid OTP");
      }
    } catch (err) {
      showMsg("error", "An error occurred");
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    if (!newPassword || newPassword !== confirmPassword) {
      setFieldErrors({ confirmPassword: "Passwords do not match." });
      return;
    }
    setLoading(true);
    setInlineMsg(null);
    try {
      const res = await resetPassword(identifier.trim(), newPassword, confirmPassword);
      if (res.Status || res.StatusCode === 200) {
        showMsg("success", res.Message || "Password Changed Successfully");
        setTimeout(() => switchTab("login"), 2000);
      } else {
        showMsg("error", res.Message || "Failed to reset password");
      }
    } catch (err) {
      showMsg("error", "An error occurred");
    } finally {
      setLoading(false);
    }
  }

  const regPwdChecks = getPasswordChecks(regPassword);

  return createPortal(
    <div className="lrm-backdrop" onClick={onClose}>
      <div className="lrm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Left Side: Luxury Image */}
        <div className="lrm-image-side">
          <div className="lrm-img-overlay" />
          <div className="lrm-brand-tag">LapStay</div>
          <div className="lrm-img-tagline">
            <h3>
              Premium Stays
              <br />
              Await You
            </h3>
            <p>Join thousands of happy travellers</p>
          </div>
        </div>

        {/* Right Side: Form */}
        <div className="lrm-form-side">
          <button
            className="lrm-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ✕
          </button>

          <h2 className="lrm-title">
            {activeTab === "login" ? "Welcome Back!" : activeTab === "register" ? "Create Account" : activeTab === "forgot" ? "Forgot Password" : activeTab === "verify_otp" ? "Verify OTP" : "Reset Password"}
          </h2>
          <p className="lrm-sub">
            {activeTab === "login"
              ? "Login to access your bookings & profile"
              : "Register to start booking premium stays"}
          </p>

          {/* Tabs */}
          {["login", "register"].includes(activeTab) && (
            <div className="lrm-tabs">
            <button
              className={`lrm-tab-btn${activeTab === "login" ? " active" : ""}`}
              onClick={() => switchTab("login")}
            >
              Login
            </button>
            <button
              className={`lrm-tab-btn${
                activeTab === "register" ? " active" : ""
              }`}
              onClick={() => switchTab("register")}
            >
              Register
            </button>
          </div>
          )}

          {/* Inline Message */}
          {inlineMsg && (
            <div
              style={{
                padding: "10px 14px",
                borderRadius: "8px",
                marginBottom: "14px",
                fontSize: "13px",
                fontWeight: "600",
                background:
                  inlineMsg.type === "success"
                    ? "rgba(7,235,14,0.12)"
                    : "rgba(255,77,77,0.12)",
                color: inlineMsg.type === "success" ? "#07EB0E" : "#FF4D4D",
                border: `1px solid ${
                  inlineMsg.type === "success"
                    ? "rgba(7,235,14,0.3)"
                    : "rgba(255,77,77,0.3)"
                }`,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {inlineMsg.type === "success" ? "✅" : "❌"} {inlineMsg.text}
            </div>
          )}

          {/* ─── LOGIN FORM ─── */}
          {activeTab === "login" && (
            <form className="lrm-form" onSubmit={handleLogin} noValidate>
              <div className="lrm-field-group">
                <span className="lrm-field-icon">✉️</span>
                <input
                  type="text"
                  placeholder="Email or Mobile Number"
                  value={identifier}
                  onChange={(e) => {
                    setIdentifier(e.target.value);
                    if (fieldErrors.identifier) {
                      setFieldErrors((f) => ({ ...f, identifier: undefined }));
                    }
                    if (inlineMsg?.type === "error") setInlineMsg(null);
                  }}
                  autoComplete="username"
                />
              </div>
              {fieldErrors.identifier && (
                <p className="lrm-field-error">{fieldErrors.identifier}</p>
              )}

              <div className="lrm-field-group">
                <span className="lrm-field-icon">🔒</span>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (fieldErrors.password) {
                      setFieldErrors((f) => ({ ...f, password: undefined }));
                    }
                    if (inlineMsg?.type === "error") setInlineMsg(null);
                  }}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="lrm-pass-toggle"
                  onClick={() => setShowPassword((p) => !p)}
                >
                  {showPassword ? "👁️" : "🙈"}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="lrm-field-error">{fieldErrors.password}</p>
              )}

              <div className="lrm-options-row">
                <label className="lrm-remember">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>
                <button type="button" className="lrm-forgot" style={{background:"none",border:"none",padding:0,font: "inherit",cursor:"pointer"}} onClick={() => switchTab("forgot")}>
                  Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                className="lrm-submit-btn"
                disabled={loading}
              >
                {loading ? <><span className="lrm-spinner" /> Logging in...</> : "Login"}
              </button>

              <p
                style={{
                  textAlign: "center",
                  color: "rgba(255,255,255,0.4)",
                  fontSize: "13px",
                  marginTop: "16px",
                }}
              >
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={() => switchTab("register")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#FFB507",
                    fontWeight: "700",
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                >
                  Register now
                </button>
              </p>
            </form>
          )}


          {/* FORGOT PASSWORD FORM */}
          {activeTab === "forgot" && (
            <form className="lrm-form" onSubmit={handleSendOTP} noValidate>
              <div className="lrm-field-group">
                <span className="lrm-field-icon">📧</span>
                <input
                  type="email"
                  placeholder="Enter Registered Email"
                  value={identifier}
                  onChange={(e) => {
                    setIdentifier(e.target.value);
                    if (fieldErrors.identifier) setFieldErrors((f) => ({ ...f, identifier: undefined }));
                  }}
                  autoComplete="email"
                />
              </div>
              {fieldErrors.identifier && (
                <p className="lrm-field-error">{fieldErrors.identifier}</p>
              )}

              <button type="submit" className="lrm-submit-btn" disabled={loading}>
                {loading ? <><span className="lrm-spinner" /> Sending OTP...</> : "Send OTP"}
              </button>
              
              <div style={{ textAlign: "center", marginTop: "15px" }}>
                <button type="button" className="lrm-forgot" style={{background:"none",border:"none",padding:0,font: "inherit",cursor:"pointer"}} onClick={() => switchTab("login")}>
                  Back to Login
                </button>
              </div>
            </form>
          )}

          {/* VERIFY OTP FORM */}
          {activeTab === "verify_otp" && (
            <form className="lrm-form" onSubmit={handleVerifyOTP} noValidate>
              <div className="lrm-field-group">
                <span className="lrm-field-icon">🔑</span>
                <input
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value);
                    if (fieldErrors.otp) setFieldErrors((f) => ({ ...f, otp: undefined }));
                  }}
                  maxLength={6}
                />
              </div>
              {fieldErrors.otp && (
                <p className="lrm-field-error">{fieldErrors.otp}</p>
              )}

              <button type="submit" className="lrm-submit-btn" disabled={loading}>
                {loading ? <><span className="lrm-spinner" /> Verifying...</> : "Verify OTP"}
              </button>
              
              <div style={{ textAlign: "center", marginTop: "15px" }}>
                <button type="button" className="lrm-forgot" style={{background:"none",border:"none",padding:0,font: "inherit",cursor:"pointer"}} onClick={() => switchTab("login")}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* RESET PASSWORD FORM */}
          {activeTab === "reset_password" && (
            <form className="lrm-form" onSubmit={handleResetPassword} noValidate>
              <div className="lrm-field-group">
                <span className="lrm-field-icon">🔒</span>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (fieldErrors.newPassword) setFieldErrors((f) => ({ ...f, newPassword: undefined }));
                  }}
                />
                <button type="button" className="lrm-pass-toggle" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? "👁️" : "🙈"}
                </button>
              </div>

              <div className="lrm-field-group">
                <span className="lrm-field-icon">🔒</span>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (fieldErrors.confirmPassword) setFieldErrors((f) => ({ ...f, confirmPassword: undefined }));
                  }}
                />
                <button type="button" className="lrm-pass-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? "👁️" : "🙈"}
                </button>
              </div>
              {fieldErrors.confirmPassword && (
                <p className="lrm-field-error">{fieldErrors.confirmPassword}</p>
              )}

              <button type="submit" className="lrm-submit-btn" disabled={loading}>
                {loading ? <><span className="lrm-spinner" /> Updating Password...</> : "Reset Password"}
              </button>

              <div style={{ textAlign: "center", marginTop: "16px" }}>
                <button
                  type="button"
                  className="lrm-forgot"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    font: "inherit",
                    cursor: "pointer",
                    color: "#FFB507",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                  onClick={() => switchTab("login")}
                >
                  ← Back to Login
                </button>
              </div>
            </form>
          )}

          {/* ─── REGISTER FORM ─── */}
          {activeTab === "register" && (
            <form className="lrm-form" onSubmit={handleRegister} noValidate>
              <div className="lrm-field-group">
                <span className="lrm-field-icon">👤</span>
                <input
                  type="text"
                  placeholder="Full Name *"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (fieldErrors.name) {
                      setFieldErrors((f) => ({ ...f, name: undefined }));
                    }
                  }}
                />
              </div>
              {fieldErrors.name && (
                <p className="lrm-field-error">{fieldErrors.name}</p>
              )}

              <div className="lrm-field-group">
                <span className="lrm-field-icon">✉️</span>
                <input
                  type="email"
                  placeholder="Email Address *"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldErrors.email) {
                      setFieldErrors((f) => ({ ...f, email: undefined }));
                    }
                  }}
                  autoComplete="email"
                />
              </div>
              {fieldErrors.email && (
                <p className="lrm-field-error">{fieldErrors.email}</p>
              )}

              <div className="lrm-field-group">
                <span className="lrm-field-icon">📞</span>
                <input
                  type="tel"
                  placeholder="Mobile Number *"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    if (fieldErrors.phone) {
                      setFieldErrors((f) => ({ ...f, phone: undefined }));
                    }
                  }}
                />
              </div>
              {fieldErrors.phone && (
                <p className="lrm-field-error">{fieldErrors.phone}</p>
              )}

              <div className="lrm-field-group">
                <span className="lrm-field-icon">🔒</span>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password * (min 8 chars)"
                  value={regPassword}
                  onChange={(e) => {
                    setRegPassword(e.target.value);
                    if (fieldErrors.regPassword) {
                      setFieldErrors((f) => ({ ...f, regPassword: undefined }));
                    }
                  }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="lrm-pass-toggle"
                  onClick={() => setShowPassword((p) => !p)}
                >
                  {showPassword ? "👁️" : "🙈"}
                </button>
              </div>
              {fieldErrors.regPassword && (
                <p className="lrm-field-error">{fieldErrors.regPassword}</p>
              )}

              {/* Password strength checklist */}
              {regPassword.length > 0 && (
                <div className="lrm-pwd-checks">
                  {regPwdChecks.map((c) => (
                    <div
                      key={c.label}
                      style={{
                        fontSize: "11px",
                        color: c.ok ? "#07EB0E" : "#ff4d4d",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginBottom: "2px",
                      }}
                    >
                      <span>{c.ok ? "✔" : "○"}</span>
                      {c.label}
                    </div>
                  ))}
                </div>
              )}

              <div className="lrm-field-group">
                <span className="lrm-field-icon">🔒</span>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password *"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (fieldErrors.confirmPassword) {
                      setFieldErrors((f) => ({
                        ...f,
                        confirmPassword: undefined,
                      }));
                    }
                  }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="lrm-pass-toggle"
                  onClick={() => setShowConfirmPassword((p) => !p)}
                >
                  {showConfirmPassword ? "👁️" : "🙈"}
                </button>
              </div>
              {fieldErrors.confirmPassword && (
                <p className="lrm-field-error">{fieldErrors.confirmPassword}</p>
              )}

              <button
                type="submit"
                className="lrm-submit-btn"
                disabled={loading}
              >
                {loading ? <><span className="lrm-spinner" /> Registering...</> : "Create Account"}
              </button>

              <p
                style={{
                  textAlign: "center",
                  color: "rgba(255,255,255,0.4)",
                  fontSize: "13px",
                  marginTop: "18px",
                  marginBottom: "24px",
                  paddingBottom: "10px",
                }}
              >
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => switchTab("login")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#FFB507",
                    fontWeight: "700",
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                >
                  Login
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
